import React, { useState, useEffect } from "react";

// Component
import Table, { TableRow } from "../../../../component/table";
import PageSelector from "../../../../component/PageSelector";
import swal from "@sweetalert/with-react";
import CustomeModal from "../../../../component/modal-popup/customeModal";
import Loader from "react-spinner-loader";
import { getDateWithFormat, isFloat } from "../../../../utils";
import RowsPerPageSelector from "../../../../component/RowsPerPageSelector";

// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  getCorrectionOrderList,
  deleteCorrectionOrderData,
} from "../../../../redux/actions/correctionOrder";

function CorrectionOrderTable({
  debouncedHandleSearch,
  update,
  setUpdate,
  updateCounter,
  setUpdateCounter,
}) {
  // State
  const [page, setPage] = useState(1);
  const [tableLoader, setTableLoader] = useState(false);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [sort, setSort] = useState();

  // Redux
  const dispatch = useDispatch();
  const { correctionOrderData } = useSelector((state) => state.correctionOrder);
  const { row_count } = useSelector((state) => state.auth);

  // Function to call GET API of Correction Order List
  const getCorrectionOrderData = () => {
    setTableLoader(true);
    const request = {
      onSuccess: () => {
        setTableLoader(false);
      },
      onError: () => {
        setTableLoader(false);
      },
    };
    dispatch(
      getCorrectionOrderList(
        request,
        page,
        debouncedHandleSearch,
        sortOrder,
        sortField,
        row_count
      )
    );
  };

  // Function to call DELETE API of Correction Order
  const handleDelete = (id) => {
    const request = {
      correction_order_id: id,
      onSuccess: () => {
        setUpdate(update + 1);
        setUpdateCounter(updateCounter + 1);
      },
      onError: (error) => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={error?.message}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    dispatch(deleteCorrectionOrderData(request));
  };

  useEffect(() => {
    getCorrectionOrderData();
  }, [update, page, debouncedHandleSearch, sortOrder, sortField, row_count]);

  useEffect(() => {
    if (sort) {
      setPage(1);
      setSortField(sort.headName);
      setSortOrder(sort.type);
    }
  }, [sort]);

  const showQuantity = (element) => {
    switch (element.correction_type) {
      case "correction_order_add": {
        return (
          <h2 className="text-sm font-semibold text-[#27AE60] leading-[100%] tracking-tight">
            +{isFloat(element.qty) ? element.qty.toFixed(2) : element.qty}
          </h2>
        );
      }

      case "correction_order_minus": {
        return (
          <h2 className="text-sm font-semibold text-[#EB5757] leading-[100%] tracking-tight">
            -{isFloat(element.qty) ? element.qty.toFixed(2) : element.qty}
          </h2>
        );
      }

      default:
        return element.qty;
    }
  };

  return (
    <div>
      <Table
        heads={[
          {
            headName: "Correction Order Number",
            isSort: true,
            value: "correction_order_no",
          },
          { headName: "SKU / Product", isSort: true, value: "item_name" },
          {
            headName: "Quantity",
            isSort: true,
            value: "qty",
          },
          { headName: "Correction Date", isSort: true, value: "createdAt" },
          { headName: "Remarks", isSort: true, value: "remarks" },
          { headName: "Action", isSort: false, value: "Action", class: "w-[100px]" },
        ]}
        tClass="table-fixed w-full border-separate border-spacing-y-2"
        hClass="text-sm leading-2 tracking-tight font-semibold text-[#1E293B] bg-[--bg-color-100]"
        bClass="text-sm"
        thClass={`px-2 py-3 whitespace-nowrap text-left font-semibold first:pl-4 first:rounded-l-md last:rounded-r-md max-w-1/4`}
        setSort={setSort}
        sort={sort}
      >
        {tableLoader ? (
          <tr className="px-2 py-3 whitespace-nowrap">
            <td colSpan={6}>
              <div className="flex justify-center p-2 my-2 whitespace-nowrap w-full">
                <Loader show={tableLoader} />
              </div>
            </td>
          </tr>
        ) : correctionOrderData?.data?.length > 0 ? (
          correctionOrderData.data.map((element, index) => {
            const {
              correction_order_id,
              correction_order_no,
              remarks,
              item_name,
              createdAt,
            } = element;
            const correctionDate = getDateWithFormat(createdAt);
            const quantity = showQuantity(element);
            return (
              <TableRow
                key={index}
                row={{
                  correction_order_no,
                  item_name,
                  quantity,
                  correctionDate,
                  remarks,
                }}
                tdClass="px-2 py-2 text-sm leading-4 tracking-tight first:pl-4 text-[#1E293B] text-left bg-[--bg-color-50] first:rounded-l-md last:rounded-r-md max-w-1/4 truncate first:font-semibold"
              >
                <td className="px-2 py-2 whitespace-nowrap text-center text-sm bg-[--bg-color-50] rounded-r-md leading-4 tracking-tight text-[--bg-color-500] font-semibold">
                  <div className="space-x-3 flex items-center justify-start ml-4">
                    <span
                      title="Delete"
                      className="fa-light fa-trash-can text-base cursor-pointer"
                      style={{ color: "#eb5757" }}
                      onClick={() => {
                        swal({
                          content: (
                            <CustomeModal
                              type="delete"
                              title="Are you sure?"
                              content="All of your data will be permanently removed from our servers forever. This action cannot be undone."
                              onDelete={() => handleDelete(correction_order_id)}
                            />
                          ),
                          buttons: false,
                          closeOnClickOutside: false,
                          closeOnEsc: false,
                        });
                      }}
                    ></span>
                  </div>
                </td>
              </TableRow>
            );
          })
        ) : (
          <tr className="px-2 py-3 whitespace-nowrap">
            <td
              colSpan={6}
              className="text-center py-2 text-lg text-[#1E293B] tracking-tight leading-4"
            >
              There are no records to display
            </td>
          </tr>
        )}
      </Table>
      {!tableLoader && (
        <div className="flex justify-between items-center">
          <RowsPerPageSelector />
          <PageSelector
            rowPerPage={row_count}
            page={page}
            setpage={(newPage) => {
              setPage(newPage);
            }}
            totalDataCount={
              correctionOrderData ? correctionOrderData.totalCount : 0
            }
          />
        </div>
      )}
    </div>
  );
}

export default CorrectionOrderTable;
